<template>
  <v-card :light="!dark">
    <v-card-text>
      <v-form ref="form" v-model="valid" @submit.prevent>
        <v-row class="d-flex align-end">
          <v-col cols="12">
            <slot></slot>
          </v-col>

          <v-col cols="12">
            <v-textarea light label="" color="green" v-model.trim="inputTitle" :rules="titleRules" :rows="2"
              :auto-grow="true" :hide-details="true" required />
          </v-col>

          <v-col cols="12" class="d-flex flex-row">
            <div v-if="editMode">
              <v-btn class="green align-self-end mr-2" @click="updateItem">Update</v-btn>
              <v-btn class="green lighten-3 align-self-end" light @click="cancelItem">Cancel</v-btn>
            </div>
            <div v-else>
              <v-btn :disabled="!inputTitle" class="green align-self-end" @click="addItem">Add</v-btn>
            </div>
          </v-col>

          <v-col cols="12">
            <draggable class="inner" :list="localItems" group="notes" @end="onDragEnd">
              <ul v-for="item in localItems" :key="item.id" class="ma-1" :class="{ disabled: editMode }">
                <li>
                  <span class="pr-2 cursor--order body-1">
                    {{ item.title }}
                  </span>
                  <v-icon small class="mr-2" @click="editItem(item.id)">mdi-pencil</v-icon>
                  <v-icon small @click="removeItem(item.id)">mdi-close</v-icon>
                </li>
              </ul>
            </draggable>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable'

/**
 * Generate a unique ID for items
 */
const generateId = (prefix = 'item') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export default {
  name: 'UnitSubItemsWithId',

  components: {
    draggable,
  },

  props: {
    // Array of objects: [{ id, title }, ...]
    value: {
      type: Array,
      default: () => [],
    },
    dark: {
      type: Boolean,
      default: false,
    },
    // Prefix for generated IDs
    idPrefix: {
      type: String,
      default: 'item',
    },
  },

  data() {
    return {
      localItems: this.cloneItems(this.value),
      valid: true,
      inputTitle: '',
      titleRules: [(v) => !!v || 'Title is required'],
      editMode: false,
      editingId: null,
    }
  },

  watch: {
    value: {
      handler(newVal) {
        // Only update if different to avoid loops
        const newStr = JSON.stringify(newVal)
        const localStr = JSON.stringify(this.localItems)
        if (newStr !== localStr) {
          this.localItems = this.cloneItems(newVal)
        }
      },
      deep: true,
    },

    localItems: {
      handler(newVal) {
        // Only emit if different from prop
        const newStr = JSON.stringify(newVal)
        const propStr = JSON.stringify(this.value)
        if (newStr !== propStr) {
          this.$emit('input', newVal)
        }
      },
      deep: true,
    },
  },

  methods: {
    cloneItems(items) {
      if (!Array.isArray(items)) return []
      return JSON.parse(JSON.stringify(items))
    },

    addItem() {
      this.validate()
      if (this.valid && this.inputTitle.trim()) {
        const newItem = {
          id: generateId(this.idPrefix),
          title: this.inputTitle.trim(),
        }
        this.localItems.push(newItem)
        this.inputTitle = ''
        this.resetValidation()
      }
    },

    editItem(id) {
      const item = this.localItems.find((i) => i.id === id)
      if (item) {
        this.editMode = true
        this.editingId = id
        this.inputTitle = item.title
      }
    },

    updateItem() {
      if (this.editingId && this.inputTitle.trim()) {
        const index = this.localItems.findIndex((i) => i.id === this.editingId)
        if (index !== -1) {
          // Update title but keep the same ID
          this.$set(this.localItems, index, {
            ...this.localItems[index],
            title: this.inputTitle.trim(),
          })
        }
      }
      this.cancelItem()
    },

    removeItem(id) {
      const index = this.localItems.findIndex((i) => i.id === id)
      if (index !== -1) {
        this.localItems.splice(index, 1)
      }
    },

    cancelItem() {
      this.inputTitle = ''
      this.editMode = false
      this.editingId = null
    },

    onDragEnd() {
      // Emit after drag reorder
      this.$emit('input', this.localItems)
    },

    validate() {
      this.$refs.form.validate()
    },

    resetValidation() {
      this.$refs.form.resetValidation()
    },
  },
}
</script>

<style lang="scss" scoped>
.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.cursor--order {
  cursor: grab;
}

ul {
  list-style: none;
  padding-left: 0;

  li {
    display: flex;
    align-items: center;
    padding: 4px 0;
  }
}
</style>
